import { useEffect, useState } from "react";
import {
  Upload,
  Check,
  Shield,
  FileText,
  User,
  Building2,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAppSelector } from "@/store/hooks";
import { useCountries, useProfile } from "@/hooks";
import {
  KycDocumentType,
  KycStatus,
  KycType,
  type BusinessKycFormData,
  type DocumentUpload,
  type KycSubmission,
  type PersonalKycFormData,
} from "@/interfaces/kyc";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import Loader from "@/components/loader/Loader";
import { queryClient } from "@/services/queryClient";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";
import VerificationLevel from "@/components/kyc/VerificationLevel";
import PersonalInfo from "@/components/kyc/PersonalInfo";
import BusinessInfo from "@/components/kyc/BusinessInfo";
import DocumentsContainer from "@/components/kyc/DocumentsContainer";
import { parsePhoneNumberFromString } from "libphonenumber-js";

type KycLevel = 1 | 2 | 3;

const KycPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";

  const navigate = useNavigate();
  const { data: countries, isLoading } = useCountries();

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError,
    refetch,
  } = useProfile(userId);

  const phoneNumber = (profile?.phoneNumber || "").replace(/^\+/, "");

  const [kycType, setKycType] = useState<KycType>(KycType.PERSONAL);
  const [activeLevel, setActiveLevel] = useState<KycLevel>(1);
  const [activeTab, setActiveTab] = useState<"info" | "documents">("info");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const personalKycData: KycSubmission | undefined =
    profile?.kycSubmissions.find(
      (kyc) => kyc.level === activeLevel && kyc.kycType === KycType.PERSONAL,
    );

  // const personalKycData: KycSubmission | undefined =
  //   profile?.kycSubmissions.find(
  //     (kyc) => kyc.level === 1 && kyc.kycType === KycType.PERSONAL,
  //   );

  const businessKycData = profile?.kycSubmissions.find(
    (kyc) => kyc.level === activeLevel && kyc.kycType === KycType.BUSINESS,
  );

  const normalizeStatus = (status: string): KycStatus => {
    switch (status) {
      case KycStatus.NEEDS_MORE_INFO:
        return KycStatus.REJECTED;
      default:
        return status as KycStatus;
    }
  };

  const getLevelStatus = (level: KycLevel): KycStatus => {
    const submission = profile?.kycSubmissions.find(
      (k) => k.level === level && k.kycType === kycType,
    );

    if (!submission) return KycStatus.DRAFT;

    return normalizeStatus(submission.status);
  };

  const isLevelApproved = (level: KycLevel) =>
    getLevelStatus(level) === KycStatus.APPROVED;

  const isLevelUnderReview = (level: KycLevel) =>
    getLevelStatus(level) === KycStatus.UNDER_REVIEW;

  const isLevel1Approved = isLevelApproved(1);

  const isBasicInfoLocked = isLevel1Approved;

  const isCurrentLevelApproved = isLevelApproved(activeLevel);
  const isCurrentLevelUnderReview = isLevelUnderReview(activeLevel);

  const isLevelInputLocked =
    isBasicInfoLocked || isCurrentLevelApproved || isCurrentLevelUnderReview;

  const isLevelUploadAllowed =
    !isCurrentLevelApproved && !isCurrentLevelUnderReview;

  // Personal Info - All original fields
  const [personalInfo, setPersonalInfo] = useState<PersonalKycFormData>({
    fullName: "",
    dateOfBirth: "",
    nationality: "",
    dialCode: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  // Business Info - All original fields
  const [businessInfo, setBusinessInfo] = useState<BusinessKycFormData>({
    businessName: "",
    registrationNumber: "",
    businessType: "",
    taxId: "",
    website: "",
    businessAddress: "",
    businessCity: "",
    businessState: "",
    businessCountry: "",
    directorName: "",
    directorIdNumber: "",
  });

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    level?: KycLevel;
    reason?: string;
  }>({ open: false });

  // Documents (aligned with backend)
  const [documents, setDocuments] = useState<
    Record<KycType, Record<KycLevel, DocumentUpload[]>>
  >({
    PERSONAL: {
      1: [
        {
          id: "p1-1",
          name: "Government ID (Passport, Driver's License, or National ID)",
          type: KycDocumentType.GOVERNMENT_ID,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "p1-2",
          name: "Selfie with ID",
          type: KycDocumentType.SELFIE_WITH_ID,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
      2: [
        {
          id: "p2-1",
          name: "Proof of Address (Utility Bill or Bank Statement)",
          type: KycDocumentType.PROOF_OF_ADDRESS,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
      3: [
        {
          id: "p3-1",
          name: "Source of Funds Declaration",
          type: KycDocumentType.SOURCE_OF_FUNDS,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "p3-2",
          name: "Bank Reference Letter",
          type: KycDocumentType.BANK_REFERENCE,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "p3-3",
          name: "Employment/Income Proof",
          type: KycDocumentType.INCOME_PROOF,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
    },
    BUSINESS: {
      1: [
        {
          id: "b1-1",
          name: "Business Registration Certificate",
          type: KycDocumentType.BUSINESS_REG_CERT,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "b1-2",
          name: "Director's ID",
          type: KycDocumentType.DIRECTOR_ID,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "b1-3",
          name: "Director's Selfie with ID",
          type: KycDocumentType.DIRECTOR_SELFIE,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
      2: [
        {
          id: "b2-1",
          name: "Tax Clearance Certificate",
          type: KycDocumentType.TAX_CLEARANCE,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "b2-2",
          name: "Business Bank Statement",
          type: KycDocumentType.BUSINESS_BANK_STATEMENT,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
      3: [
        {
          id: "b3-1",
          name: "Audited Financial Statements",
          type: KycDocumentType.AUDITED_FINANCIALS,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
        {
          id: "b3-2",
          name: "Trade License",
          type: KycDocumentType.TRADE_LICENSE,
          file: null,
          preview: null,
          url: null,
          status: "pending",
        },
      ],
    },
  });

  useEffect(() => {
    if (!profile) return;

    const profilePhone = profile.phoneNumber ?? "";
    const parsedPhone = parsePhoneNumberFromString(profilePhone);

    setPersonalInfo((prev) => ({
      ...prev,
      fullName: `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim(),

      // Keep only the national/local number in the input
      phoneNumber: parsedPhone?.nationalNumber ?? "",

      // Keep country calling code separately
      dialCode: parsedPhone?.countryCallingCode
        ? `+${parsedPhone.countryCallingCode}`
        : prev.dialCode,
    }));
  }, [profile]);

  useEffect(() => {
    if (kycType === KycType.PERSONAL && personalKycData) {
      setPersonalInfo(personalKycData.formData.formData as PersonalKycFormData);

      const level = personalKycData.level as KycLevel;

      setDocuments((prev) => ({
        ...prev,
        PERSONAL: {
          ...prev.PERSONAL,
          [level]: prev.PERSONAL[level].map((existingDoc) => {
            const uploaded = personalKycData.documents.find(
              (doc) => doc.documentType === existingDoc.type,
            );

            if (!uploaded) return existingDoc;

            return {
              ...existingDoc,
              url: uploaded.documentUrl,
              preview: uploaded.documentUrl,
              metadata: uploaded.metadata,
              status: "uploaded",
              file: null,
            };
          }),
        },
      }));
    }

    if (kycType === KycType.BUSINESS && businessKycData) {
      setBusinessInfo(businessKycData.formData.formData as BusinessKycFormData);

      const level = businessKycData.level as KycLevel;

      setDocuments((prev) => ({
        ...prev,
        BUSINESS: {
          ...prev.BUSINESS,
          [level]: prev.BUSINESS[level].map((existingDoc) => {
            const uploaded = businessKycData.documents.find(
              (doc) => doc.documentType === existingDoc.type,
            );

            if (!uploaded) return existingDoc;

            return {
              ...existingDoc,
              url: uploaded.documentUrl,
              preview: uploaded.documentUrl,
              metadata: uploaded.metadata,
              status: "uploaded",
              file: null,
            };
          }),
        },
      }));
    }
  }, [kycType, personalKycData, businessKycData]);

  const getInternationalPhone = (): string | undefined => {
    const localNumber = personalInfo.phoneNumber?.trim();
    const dialCode = personalInfo.dialCode?.trim();

    if (!localNumber) return undefined;

    // If the number is already E.164, don't modify it
    if (localNumber.startsWith("+")) {
      return localNumber;
    }

    if (!dialCode) {
      return undefined;
    }

    return `${dialCode}${localNumber}`.replace(/[^\d+]/g, "");
  };

  const handleSubmit = async () => {
    const currentDocs = documents[kycType][activeLevel];
    const uploadedDocs = currentDocs.filter(
      (d) => d.status === "uploaded" && d.url,
    );

    let resolvedPhone: string | undefined;

    if (!isLevelUploadAllowed) {
      toast.warning("Error", {
        description: "This level is locked and cannot be submitted",
      });
      return;
    }

    if (uploadedDocs.length !== currentDocs.length) {
      toast.warning("Error", {
        description: "Please upload all required documents",
      });
      return;
    }

    // Validation
    if (kycType === KycType.PERSONAL) {
      resolvedPhone = getInternationalPhone();

      const resolvedFullName =
        personalInfo.fullName ||
        `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim();

      if (activeLevel === 1 && !personalInfo.nationality) {
        toast.warning("Error", {
          description: "Nationality is required!",
        });

        return;
      }

      if (
        !resolvedFullName ||
        !personalInfo.dateOfBirth ||
        !personalInfo.nationality ||
        !resolvedPhone ||
        !personalInfo.address
      ) {
        toast.warning("Error", {
          description: "Please fill in all required personal information",
        });

        setActiveTab("info");
        return;
      }

      const parsedPhone = parsePhoneNumberFromString(resolvedPhone);

      if (!parsedPhone?.isValid()) {
        toast.warning("Invalid phone number", {
          description: "Please enter a valid phone number.",
        });

        setActiveTab("info");
        return;
      }
    } else {
      if (
        !businessInfo.businessName ||
        !businessInfo.registrationNumber ||
        !businessInfo.businessType ||
        !businessInfo.businessAddress
      ) {
        toast.warning("Error", {
          description: "Please fill in all required business information",
        });
        setActiveTab("info");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        kycType,
        level: activeLevel,
        documents: uploadedDocs.map((doc) => ({
          type: doc.type,
          url: doc.url!,
          metadata: {
            originalName: doc.file?.name,
            size: doc.file?.size,
          },
        })),
        formData: {
          kycType,
          formData:
            kycType === KycType.PERSONAL
              ? {
                  ...personalInfo,
                  phoneNumber: resolvedPhone,
                  nationalityId: personalInfo.nationality,
                }
              : businessInfo,
        },
      };

      console.log("Submitting KYC:", payload);

      const res = await makeRequest.post("/kyc-submission", payload);

      if (res) {
        toast.success("Success!", {
          description: `Level ${activeLevel} ${kycType} KYC submitted for review.`,
        });

        queryClient.invalidateQueries({ queryKey: ["profile"] });
      }
      setIsSubmitting(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const getProgress = () => {
    const current = documents[kycType][activeLevel];
    return (
      (current.filter((d) => d.status === "uploaded").length / current.length) *
      100
    );
  };

  if (isLoading || isLoadingProfile) return <Loader type="bounce" />;

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6 mt-10">
      <div className="max-w-5xl mx-auto">
        <Button
          className="hidden md:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between mb-6">
          {profile?.kycStatus === KycStatus.APPROVED && (
            <Badge className="bg-emerald-500/20 text-emerald-500">
              <Check className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>

        {/* KYC Type Selector */}
        <div className="bg-card border border-border rounded-xl p-1 mb-6">
          <div className="flex gap-1">
            <button
              onClick={() => {
                setKycType(KycType.PERSONAL);
                setActiveLevel(1);
              }}
              className={cn(
                "flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2",
                kycType === KycType.PERSONAL
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              <User className="w-4 h-4" /> Personal KYC
            </button>
            <button
              onClick={() => {
                setKycType(KycType.BUSINESS);
                setActiveLevel(1);
              }}
              className={cn(
                "flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2",
                kycType === KycType.BUSINESS
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              <Building2 className="w-4 h-4" /> Business KYC
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div
          className={cn(
            "bg-card border rounded-xl p-6 mb-6",
            profile?.kycStatus === KycStatus.APPROVED
              ? "border-emerald-500/30"
              : "border-border",
          )}
        >
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "w-14 h-14 rounded-full flex items-center justify-center",
                profile?.kycStatus === KycStatus.APPROVED
                  ? "bg-emerald-500/10"
                  : "bg-muted",
              )}
            >
              {profile?.kycStatus === KycStatus.APPROVED ? (
                <Check className="w-7 h-7 text-emerald-500" />
              ) : (
                <Shield className="w-7 h-7 text-muted-foreground" />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-foreground">
                {kycType === KycType.PERSONAL ? "Personal" : "Business"} KYC
                Status:{" "}
                <span
                  className={
                    profile?.kycStatus === KycStatus.APPROVED
                      ? "text-emerald-500"
                      : "text-muted-foreground"
                  }
                >
                  {profile?.kycStatus === KycStatus.REJECTED
                    ? "Action Required"
                    : profile?.kycStatus || "NOT STARTED"}
                </span>
              </h2>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Sidebar - Levels */}
          <VerificationLevel
            activeLevel={activeLevel}
            setActiveLevel={setActiveLevel}
            kycType={kycType}
            setKycType={setKycType}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b border-border">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">
                    Document Upload Progress
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(getProgress())}%
                  </span>
                </div>
                <Progress value={getProgress()} className="h-2" />
              </div>

              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab("info")}
                  className={cn(
                    "flex-1 py-3 text-sm font-medium",
                    activeTab === "info"
                      ? "border-b-2 border-primary text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  Info
                </button>
                <button
                  onClick={() => setActiveTab("documents")}
                  className={cn(
                    "flex-1 py-3 text-sm font-medium",
                    activeTab === "documents"
                      ? "border-b-2 border-primary text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  Documents
                </button>
              </div>

              <div className="p-6">
                {activeTab === "info" ? (
                  kycType === KycType.PERSONAL ? (
                    <PersonalInfo
                      profile={profile}
                      countries={countries}
                      isLevelInputLocked={isLevelInputLocked}
                      personalKyc={personalInfo}
                      setPersonalKyc={setPersonalInfo}
                    />
                  ) : (
                    <BusinessInfo
                      isLevelInputLocked={isLevelInputLocked}
                      businessInfo={businessInfo}
                      setBusinessInfo={setBusinessInfo}
                      countries={countries}
                      isLoading={isLoading}
                    />
                  )
                ) : (
                  // Documents Section
                  <DocumentsContainer
                    isLevelUploadAllowed={isLevelUploadAllowed}
                    documents={documents}
                    setDocuments={setDocuments}
                    kycType={kycType}
                    activeLevel={activeLevel}
                  />
                )}

                <div className="mt-8 pt-6 border-t">
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isLevelUploadAllowed}
                    size="lg"
                    className="w-full"
                  >
                    {isSubmitting ? (
                      <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-5 w-5" />
                    )}
                    {isSubmitting
                      ? "Submitting..."
                      : `Submit Level ${activeLevel} for Review`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={actionModal.open}
        onOpenChange={(open) => setActionModal({ open })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Action Required
            </DialogTitle>
            <DialogDescription>
              Your submission needs correction before approval.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm">
            {actionModal.reason}
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              className="flex-1"
              onClick={() => {
                setActiveLevel(actionModal.level!);
                setActiveTab("documents");
                setActionModal({ open: false });
              }}
            >
              Fix Now
            </Button>

            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setActionModal({ open: false })}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KycPage;
